<template>
    <div class="ds-employees-content">
        <EmployeesToolbar
            :segment="segment"
            v-model:search="searchInput"
            v-model:position-ids="positionIds"
            v-model:department-ids="departmentIds"
            :active-count="counts.active"
            :archive-count="counts.archive"
            :can-view-archive="canViewArchive"
            :can-create="canCreate"
            :position-options="positionOptions"
            :department-options="departmentOptions"
            @update:segment="onSegmentChange"
            @create="emit('create')"
        />
        <EmployeesFilterChips
            v-if="filterChips.length"
            :chips="filterChips"
            :found="counts[segment]"
            :total="unfilteredCount"
            @remove="removeChip"
            @reset-all="resetCategoryFilters"
        />
        <div class="ds-employees-table" role="table" :aria-label="isArchive ? 'Архив сотрудников' : 'Активные сотрудники'" :aria-busy="status === 'loading'">
            <div role="rowgroup">
                <div role="row" class="ds-employees-grid ds-employees-header" :class="gridClass">
                    <span role="columnheader">Сотрудник</span>
                    <span role="columnheader">Подразделение</span>
                    <span v-if="isArchive" role="columnheader">Уволен</span>
                    <template v-else>
                        <span role="columnheader" class="ds-employees-ellipsis" :title="hoursHeader">{{ hoursHeader }}</span>
                        <span role="columnheader">Нарушения</span>
                    </template>
                    <span role="columnheader">Рейтинг</span>
                </div>
            </div>
            <div role="rowgroup" class="ds-employees-body">
                <template v-if="status === 'loading'">
                    <div v-for="index in SKELETON_ROWS" :key="index" role="row" class="ds-employees-grid ds-employees-skeleton-row" :class="gridClass" aria-hidden="true">
                        <span class="ds-employees-person">
                            <span class="ds-employees-skeleton ds-employees-skeleton-avatar"></span>
                            <span class="ds-employees-person-text">
                                <span class="ds-employees-skeleton ds-employees-skeleton-line w-[180px]"></span>
                                <span class="ds-employees-skeleton ds-employees-skeleton-line w-[120px]"></span>
                            </span>
                        </span>
                        <span class="ds-employees-skeleton ds-employees-skeleton-line w-[140px]"></span>
                        <span class="ds-employees-skeleton ds-employees-skeleton-line w-[120px]"></span>
                        <span v-if="!isArchive" class="ds-employees-skeleton ds-employees-skeleton-line w-[40px]"></span>
                        <span class="ds-employees-skeleton ds-employees-skeleton-line w-[64px]"></span>
                    </div>
                </template>
                <div v-else-if="status === 'error'" class="ds-employees-state" role="row">
                    <div role="cell" class="ds-employees-state-content" aria-live="polite">
                        <span class="ds-employees-state-title">Не удалось загрузить сотрудников</span>
                        <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="retry">Повторить</button>
                    </div>
                </div>
                <div v-else-if="!rows.length" class="ds-employees-state" role="row">
                    <div role="cell" class="ds-employees-state-content">
                        <template v-if="hasConditions">
                            <span class="ds-employees-state-title">Никого не нашли</span>
                            <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="resetFilters">Сбросить фильтры</button>
                        </template>
                        <span v-else class="ds-employees-state-title">{{ isArchive ? 'В архиве пока никого' : 'Пока нет сотрудников' }}</span>
                    </div>
                </div>
                <template v-else>
                    <EmployeesListItem
                        v-for="employee in rows"
                        :key="employee.id"
                        :employee="employee"
                        :archive="isArchive"
                        @user="emit('user-click', { employee, archive: isArchive })"
                    />
                </template>
            </div>
        </div>
        <Pager
            v-if="status !== 'error'"
            :total="total"
            :page="page"
            :page-size="pageSize"
            @update:page="onPageChange"
            @update:page-size="onPageSizeChange"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useAuthStore } from '@/stores/authStore'
import { getUsers, getUsersFired } from '@/api/users'
import EmployeesListItem from './EmployeesListItem.vue'
import Pager from './Pager.vue'
import EmployeesToolbar from './EmployeesToolbar.vue'
import EmployeesFilterChips from './EmployeesFilterChips.vue'
import { monthNameFromSchedule } from './employeeHours'
import type { EmployeeRow, EmployeesSegment, FilterChip, FilterOption, PageSize, UsersPage } from './employeesList'

type ListStatus = 'loading' | 'ready' | 'error'
type QueryParams = Record<string, string | number>

const SEARCH_DELAY = 1000
const SKELETON_ROWS = 8
const DEFAULT_PAGE_SIZE: PageSize = 15

const emit = defineEmits<{
    (e: 'user-click', payload: { employee: EmployeeRow, archive: boolean }): void
    (e: 'create'): void
}>()

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const authStore = useAuthStore()

const canViewArchive = computed(() => Boolean(authStore.permissions?.can_view_user_archive))
const canCreate = computed(() => Boolean(authStore.permissions?.can_create_user))

const positionOptions = computed<FilterOption[]>(() => scheduleStore.positions.map(({ id, name }) => ({ id, name })))
const departmentOptions = computed<FilterOption[]>(() => departmentStore.myDepartments.map(({ id, name }) => ({ id, name })))

// Месяц часов — month_schedule.month из ответа (локальный месяц подразделения), без выбора периода.
// Пока месяц неизвестен (загрузка, backend без RD-154) — нейтральная подпись.
const hoursMonth = ref<string | null>(null)
const hoursHeader = computed(() => `Часы за ${hoursMonth.value ?? 'текущий месяц'} · факт / норма`)

const segment = ref<EmployeesSegment>('active')
const searchInput = ref('')
const searchText = ref('')
const positionIds = ref<string[]>([])
const departmentIds = ref<string[]>([])
const page = ref(1)
const pageSize = ref<PageSize>(DEFAULT_PAGE_SIZE)

const rows = ref<EmployeeRow[]>([])
const total = ref(0)
const status = ref<ListStatus>('loading')
// Счётчики сегментов по текущему поиску/фильтрам; null — ещё не загружен или ошибка.
const counts = reactive<Record<EmployeesSegment, number | null>>({ active: null, archive: null })

// M для «Найдено N из M»: count сегмента с тем же поиском, но без фильтров должности/подразделения.
// Кэш по сегменту и поиску; сбрасывается при перезагрузке данных.
const unfilteredCounts = ref<Record<string, number>>({})
let unfilteredVersion = 0

// Номера последних запросов: ответ устаревшего запроса не применяется.
let listRequestId = 0
const countRequestIds: Record<EmployeesSegment, number> = { active: 0, archive: 0 }
let searchTimer: ReturnType<typeof setTimeout> | undefined

const isArchive = computed(() => segment.value === 'archive')
const gridClass = computed(() => isArchive.value ? 'ds-employees-grid-archive' : 'ds-employees-grid-active')
const hasCategoryFilters = computed(() => Boolean(positionIds.value.length || departmentIds.value.length))
const hasConditions = computed(() => Boolean(searchText.value || hasCategoryFilters.value))

const unfilteredKey = (target: EmployeesSegment, search: string) => `${target}|${search}`
const unfilteredCount = computed<number | null>(() => unfilteredCounts.value[unfilteredKey(segment.value, searchText.value)] ?? null)

// Чипы: сначала должности, затем подразделения, внутри — порядок справочника.
const filterChips = computed<FilterChip[]>(() => [
    ...positionOptions.value.filter(option => positionIds.value.includes(option.id)).map(option => ({ ...option, kind: 'position' as const })),
    ...departmentOptions.value.filter(option => departmentIds.value.includes(option.id)).map(option => ({ ...option, kind: 'department' as const }))
])
const otherSegment = computed<EmployeesSegment>(() => isArchive.value ? 'active' : 'archive')

const canLoadSegment = (target: EmployeesSegment) => target === 'active' || canViewArchive.value

const buildParams = (): QueryParams => {
    const params: QueryParams = {}
    if (searchText.value) params.search = searchText.value
    if (positionIds.value.length) params.position_ids = positionIds.value.join(',')
    if (departmentIds.value.length) params.department_ids = departmentIds.value.join(',')
    return params
}

const fetchPage = async (target: EmployeesSegment, params: QueryParams): Promise<UsersPage> => {
    const res = target === 'archive' ? await getUsersFired(params) : await getUsers(params)
    const body = res.data?.body
    if (!body || !Array.isArray(body.results) || typeof body.count !== 'number') {
        throw new Error('Неожиданный формат ответа списка сотрудников')
    }
    return { count: body.count, results: body.results }
}

const rememberUnfilteredCount = (target: EmployeesSegment, search: string, count: number) => {
    unfilteredCounts.value = { ...unfilteredCounts.value, [unfilteredKey(target, search)]: count }
}

async function loadUnfilteredCount() {
    if (!hasCategoryFilters.value) return
    const target = segment.value
    if (!canLoadSegment(target)) return
    const search = searchText.value
    if (unfilteredKey(target, search) in unfilteredCounts.value) return
    const version = unfilteredVersion
    try {
        const data = await fetchPage(target, search ? { search, page: 1, page_size: 1 } : { page: 1, page_size: 1 })
        if (version === unfilteredVersion) rememberUnfilteredCount(target, search, data.count)
    } catch (err) {
        console.error('Не удалось загрузить общее количество сотрудников', err)
    }
}

async function loadList() {
    const requestId = ++listRequestId
    const target = segment.value
    if (!canLoadSegment(target)) return
    // Список сам обновляет счётчик своего сегмента — отдельный запрос счётчика больше не нужен.
    countRequestIds[target]++

    status.value = 'loading'
    rows.value = []
    // Пока идёт загрузка, итог пагинации — последний известный count этого сегмента.
    if (counts[target] !== null) total.value = counts[target]

    const params = buildParams()

    try {
        const data = await fetchPage(target, { ...params, page: page.value, page_size: pageSize.value })
        if (requestId !== listRequestId) return
        counts[target] = data.count
        total.value = data.count
        if (!params.position_ids && !params.department_ids) rememberUnfilteredCount(target, String(params.search ?? ''), data.count)
        // Страница вышла за пределы выборки (записей стало меньше) — перейти на последнюю.
        if (!data.results.length && data.count > 0 && page.value > 1) {
            page.value = Math.ceil(data.count / pageSize.value)
            await loadList()
            return
        }
        rows.value = data.results
        if (target === 'active') {
            const schedule = data.results.find(row => row.month_schedule?.month)?.month_schedule
            hoursMonth.value = monthNameFromSchedule(schedule?.month)
        }
        status.value = 'ready'
    } catch (err) {
        if (requestId !== listRequestId) return
        console.error('Не удалось загрузить список сотрудников', err)
        counts[target] = null
        total.value = 0
        status.value = 'error'
    }
}

async function loadCount(target: EmployeesSegment) {
    if (!canLoadSegment(target)) return
    const requestId = ++countRequestIds[target]
    counts[target] = null
    try {
        const data = await fetchPage(target, { ...buildParams(), page: 1, page_size: 1 })
        if (requestId === countRequestIds[target]) counts[target] = data.count
    } catch (err) {
        if (requestId !== countRequestIds[target]) return
        console.error('Не удалось загрузить количество сотрудников', err)
        counts[target] = null
    }
}

const reloadWithCounts = () => {
    loadList()
    loadCount(otherSegment.value)
    loadUnfilteredCount()
}

// Поиск, фильтры: страница 1, обновить список и счётчик другого сегмента.
watch([searchText, positionIds, departmentIds], () => {
    page.value = 1
    // Прежний count относится к другой выборке — до ответа показывается «—».
    counts[segment.value] = null
    reloadWithCounts()
})

watch(searchInput, value => {
    clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
        searchText.value = value.trim()
    }, SEARCH_DELAY)
})

const onSegmentChange = (value: EmployeesSegment) => {
    if (value === segment.value || !canLoadSegment(value)) return
    segment.value = value
    page.value = 1
    loadList()
    loadUnfilteredCount()
}

const onPageChange = (value: number) => {
    page.value = value
    loadList()
}

// Только 15/30; любое прежнее значение (в т.ч. «Все») нормализуется к 15.
const onPageSizeChange = (value: PageSize) => {
    pageSize.value = value === 30 ? 30 : DEFAULT_PAGE_SIZE
    page.value = 1
    loadList()
}

const resetFilters = () => {
    clearTimeout(searchTimer)
    searchInput.value = ''
    searchText.value = ''
    positionIds.value = []
    departmentIds.value = []
}

// «Сбросить всё» в строке чипов: снимает оба фильтра, поиск сохраняется.
const resetCategoryFilters = () => {
    positionIds.value = []
    departmentIds.value = []
}

const removeChip = (chip: FilterChip) => {
    if (chip.kind === 'position') positionIds.value = positionIds.value.filter(id => id !== chip.id)
    else departmentIds.value = departmentIds.value.filter(id => id !== chip.id)
}

const retry = () => {
    loadList()
    if (counts[otherSegment.value] === null) loadCount(otherSegment.value)
    loadUnfilteredCount()
}

// Обновить выборку и счётчики без сброса поиска, фильтров и страницы (после изменений в карточке).
const reload = () => {
    unfilteredVersion++
    unfilteredCounts.value = {}
    reloadWithCounts()
}

defineExpose({ reload })

onMounted(() => {
    reloadWithCounts()
})

onBeforeUnmount(() => {
    clearTimeout(searchTimer)
})
</script>
